import { useState, useEffect } from "react";
import { clientID, clientSecret, expressURL, getCookie, setCookie } from "./Constants";
import { Keycloak } from "keycloak-auth";
import { IQuestion } from "../Interfaces/AipenJobs";
import { getFeedback, checkAipenRole } from "../services/AIpen";
import { IFeedback } from "../Interfaces/IFeedback";
import { verify } from "../services/Recaptcha";
import * as Constants from "../lib/Constants";
import { areas } from "../components/AreasGrid";
import { IProdotto } from "../Interfaces/IProdotto";


interface IResult {
    status: number;
}

// Hook
export const useWindowSize = (): number => {
    // Initialize state with undefined width/height so server and client renders match
    // Learn more here: https://joshwcomeau.com/react/the-perils-of-rehydration/
    const [windowSize, setWindowSize] = useState<number>(undefined);

    useEffect(() => {
        // Handler to call on window resize
        function handleResize() {
            // Set window width/height to state
            setWindowSize(window.innerWidth);
        }

        // Add event listener
        window.addEventListener("resize", handleResize);

        // Call handler right away so state gets updated with initial window size
        handleResize();

        // Remove event listener on cleanup
        return () => window.removeEventListener("resize", handleResize);
    }, []); // Empty array ensures that effect is only run on mount

    return windowSize;
};

// mappa il nome abbreviato dell'area con il rispettivo intero
export const replaceMenuName = (prodotto: IProdotto, type: string = "category"): string => {
    if(type === "areas") {
        return areas.find((a) => a.id === prodotto.id.toString()) ? areas.find((a) => a.id === prodotto.id.toString())!.areaLabel.join(",") : prodotto.descrizione;
    }
    return areas.find((a) => a.id === prodotto.id.toString()) ? areas.find((a) => a.id === prodotto.id.toString())!.category : prodotto.descrizione;
};

// mappa il nome abbreviato dell'area con il rispettivo intero
export const replaceAreaName = (data: string): string => {
    data = data.toLowerCase();
    if (data === "LM Bilancio".toLowerCase()) return "Bilancio e Contabilità,Tributi";
    if (data === "LM Personale".toLowerCase()) return "Personale e organizzazione";
    if (data === "LM Amministrazione".toLowerCase()) return "Amministrazione e PA Digitale";
    if (data === "LM Polizia".toLowerCase()) return "Polizia,Attività economiche";
    if (data === "LM Servizi Sociali".toLowerCase()) return "Servizi Sociali e Scuola";
    if (data === "LM Ufficio Tecnico".toLowerCase()) return "Ufficio Tecnico";
    if (data === "LM Demografici".toLowerCase()) return "Servizi Demografici";
    if (data === "LM Appalti".toLowerCase()) return "Appalti e Contratti";
};

// scrolla in cima alla pagina
export const scrollTop = () => {
    window.scrollTo({
        top: 0,
        behavior: "smooth",
    });
};

// controlla la risposta del server
export const serverResponseOK = (result: IResult): boolean => {
    return result.status === 200;
};

export const checkIP = async (ip: string, token: string, idProdotti: string[]) => {
    const formData = new URLSearchParams();
    const headerList = new Headers();
    headerList.append("Content-Type", "application/x-www-form-urlencoded");
    headerList.append("Accept", "*/*");
    headerList.append("Authorization", "Bearer " + token);
    const response = await fetch(`${expressURL}/api/users/ip-check`, {
        method: "POST",
        headers: headerList,
        body: formData,
    });

    if (!response.ok) {
        return null;
    }
    const data = await response.json();
    return data;
};

export const enableIP = async (ip: string, token: string, email: string, subID: string, idProdotti: string[]) => {
    const formData = new URLSearchParams();
    const headerList = new Headers();
    headerList.append("Content-Type", "application/x-www-form-urlencoded");
    headerList.append("Accept", "*/*");
    headerList.append("Authorization", "Bearer " + token);
    formData.append("email", email);
    formData.append("sub", subID);
    const response = await fetch(`${expressURL}/api/users/ip-enable`, {
        method: "POST",
        headers: headerList,
        body: formData,
    });
    return response.ok;
};

export const introspect = async (access_token: string) => {
    const formSub = new URLSearchParams();
    const headersList = {
        "Accept": "*/*",
        "Authorization": "Bearer " + access_token,
        "Content-Type": "application/x-www-form-urlencoded",
    };
    formSub.append("client_id", clientID);
    formSub.append("client_secret", clientSecret);

    const response = await fetch("https://sso.maggioli.com/realms/maggioli/protocol/openid-connect/userinfo", {
        method: "POST",
        body: formSub,
        headers: headersList,
    });
    if (!response.ok) {
        return false;
    }
    const data = await response.json();
    return data;
};

export const deleteAllCookies = () => {
    setCookie("access_token", "", -1);
    setCookie("refresh_token", "", -1);
    setCookie("api-pa-token", "", -1);
    setCookie("user_on_aipen", "", -1);
};

export const saveFile = (blob: Blob, filename: string) => {
    const a = document.createElement("a");
    document.body.appendChild(a);
    const url = window.URL.createObjectURL(blob);
    a.href = url;
    a.download = filename;
    a.click();
    setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    }, 0);
};

export const getAccessToken = async () => {
    let accessToken = getCookie("access_token");
    const refreshToken = getCookie("refresh_token");

    if (refreshToken === null) {
        return false;
    }
    if (accessToken === null) {
        accessToken = await loadTokens(refreshToken, true);
    }
    return accessToken;
};

const loadTokens = async (token: string, refresh: boolean = false) => {
    const dataKey = await Keycloak.auth().getToken(token, refresh);
    if (!dataKey.access_token) {
        return null;
    }

    setCookie("access_token", dataKey.access_token, dataKey.expires_in);
    setCookie("refresh_token", dataKey.refresh_token, dataKey.refresh_expires_in);

    return dataKey.access_token;
};

export const userOnAipen = async () => {
    const oneMonthInSeconds = 60 * 60 * 24 * 31;
    const userOnAipenCookie: null | string = Constants.getCookie("user_on_aipen");
    let isUserAipen = userOnAipenCookie !== null ? userOnAipenCookie === "true" : false;
    
    if(userOnAipenCookie === null) {
        const res1 = await checkAipenRole();
        Constants.setCookie("user_on_aipen", res1.data.result, oneMonthInSeconds);
        isUserAipen = res1.data.result;
    }
    return isUserAipen;

}

export const checkFeedback = async (jobID: number, givenFeedback: IFeedback[], callback: Function) => {
    let isUserAipen = await userOnAipen();
    if(!isUserAipen){
        return;
    }

    if(givenFeedback.filter(f => f.jobID === jobID).length > 0) {
        return;
    }
    const res = await getFeedback(jobID);
    if (res.status !== 200) {
        return;
    }
    if(!res.data.feedback) {
        return;
    }
    callback(res.data.feedback, jobID);
}

export const checkExistingFeedback = (jobID: number, givenFeedback: IFeedback[]): IFeedback | false => {
    return givenFeedback.filter(f => f.jobID === jobID).length === 1 ? givenFeedback.filter(f => f.jobID === jobID).pop() : false;
}

export const givenFeedbackIsUp = (feedback: IFeedback | false) => {
    if (feedback && feedback.vote) {
        return " text-brand-maggioli-07";
    }
    return "";
}

export const givenFeedbackIsDown = (feedback: IFeedback | false) => {
    if (feedback && !feedback.vote) {
        return " text-brand-maggioli-07";
    }
    return "";
}

export const verifyReCaptcha = async (executeRecaptcha: (action?: string) => Promise<string>) => {
    if (!executeRecaptcha) {
        console.log("reCAPTCHA non ancora disponibile");
        return false;
    }
    const token = await executeRecaptcha("form_submit");
    const res = await verify({ token: token });
    return res;
}

